/* Product compare (espresso machines + grinders) — used by sections/machine-compare.liquid
 * Data per product comes from /products/{handle}?view=compare-data
 * Search uses Shopify's Predictive Search API across all configured categories.
 * URL state: ?machines=a,b&grinders=c,d&tab=grinders
 */
(() => {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const YES = /^(yes|true)$/i;
  const NO = /^(no|false)$/i;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const el = (tag, attrs = {}, ...kids) => {
    const n = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null || v === false) continue;
      if (k === 'class') n.className = v;
      else if (k === 'text') n.textContent = v;
      else n.setAttribute(k, v === true ? '' : String(v));
    }
    kids.flat().forEach((c) => { if (c != null) n.append(c); });
    return n;
  };

  const lower = (s) => String(s || '').trim().toLowerCase();
  const splitList = (s) => (s || '').split(',').map(lower).filter(Boolean);
  const isBlank = (v) => v == null || (typeof v === 'string' && v.trim() === '');
  const keyOf = (v) => {
    if (isBlank(v)) return '';
    if (typeof v === 'object') return lower(v.label ?? v.value ?? JSON.stringify(v));
    return lower(v);
  };
  const sized = (url, w) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=' + w : url);
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const checkIcon = () => {
    const ns = 'http://www.w3.org/2000/svg';
    const s = document.createElementNS(ns, 'svg');
    s.setAttribute('viewBox', '0 0 20 20');
    s.setAttribute('class', 'mc__check');
    s.setAttribute('aria-hidden', 'true');
    s.setAttribute('focusable', 'false');
    const p = document.createElementNS(ns, 'path');
    p.setAttribute('d', 'M4 10.5l4 4 8-9');
    p.setAttribute('fill', 'none');
    p.setAttribute('stroke', 'currentColor');
    p.setAttribute('stroke-width', '2.25');
    p.setAttribute('stroke-linecap', 'round');
    p.setAttribute('stroke-linejoin', 'round');
    s.append(p);
    return s;
  };

  class ProductCompare {
    constructor(root) {
      if (root.dataset.mcReady) return;
      root.dataset.mcReady = '1';
      this.root = root;
      this.max = parseInt(root.dataset.max, 10) || 4;
      this.exclude = splitList(root.dataset.exclude);

      const parse = (sel, fallback) => {
        try { return JSON.parse(root.querySelector(sel).textContent); } catch (e) { return fallback; }
      };
      this.cats = parse('[data-categories]', []).map((c) => ({ ...c, types: (c.types || []).map(lower).filter(Boolean) }));
      if (!this.cats.length) this.cats = [{ id: 'machines', label: 'Espresso machines', noun: 'machine', nounPlural: 'machines', types: [] }];
      this.rows = parse('[data-rows]', []).filter((r) => r.source);

      this.sel = Object.fromEntries(this.cats.map((c) => [c.id, []]));
      this.tab = this.cats[0].id;
      this.cache = new Map();
      this.diffOnly = false;
      this.active = -1;
      this.term = '';

      const q = (s) => root.querySelector(s);
      this.input = q('[data-search]');
      this.list = q('[data-results]');
      this.status = q('[data-status]');
      this.panel = q('[data-panel]');
      this.table = q('[data-table]');
      this.scroller = q('[data-scroller]');
      this.empty = q('[data-empty]');
      this.emptyTitle = q('[data-empty-title]');
      this.toolbar = q('[data-toolbar]');
      this.count = q('[data-count]');
      this.suggest = q('[data-suggest]');
      this.diff = q('[data-diff]');
      this.clearBtn = q('[data-clear]');
      this.tabs = [...root.querySelectorAll('[data-tab]')];
      this.caption = this.table.querySelector('caption');
      this.thead = el('thead');

      this.bind();
      this.loadFromUrl();
    }

    get cat() { return this.cats.find((c) => c.id === this.tab) || this.cats[0]; }
    get items() { return this.sel[this.tab]; }
    catById(id) { return this.cats.find((c) => c.id === id); }
    catForType(type) { const t = lower(type); return this.cats.find((c) => c.types.includes(t)); }
    allHandles() { return new Set(Object.values(this.sel).flat().map((m) => m.handle)); }

    /* ---------- events ---------- */
    bind() {
      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(this.input.value.trim()), 200);
      });
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.input.addEventListener('blur', () => setTimeout(() => this.closeList(), 120));
      this.list.addEventListener('mousedown', (e) => {
        const opt = e.target.closest('[role="option"]');
        if (opt) { e.preventDefault(); this.pick(opt); }
      });

      this.root.addEventListener('click', (e) => {
        const add = e.target.closest('[data-add-handle]');
        if (add) { this.add(add.dataset.addHandle, add.dataset.addCat); return; }
        const rm = e.target.closest('[data-remove]');
        if (rm) { this.remove(rm.dataset.remove); return; }
        const tab = e.target.closest('[data-tab]');
        if (tab) { this.setTab(tab.dataset.tab); return; }
        if (e.target.closest('[data-focus-search]')) {
          this.input.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
          this.input.focus({ preventScroll: true });
        }
      });

      this.tabs.forEach((t, i) => t.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const next = this.tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length];
        this.setTab(next.dataset.tab);
        next.focus();
      }));

      this.diff.addEventListener('change', () => { this.diffOnly = this.diff.checked; this.renderBody(); });
      this.root.querySelector('[data-share]').addEventListener('click', (e) => this.share(e.currentTarget));
      this.clearBtn.addEventListener('click', () => {
        this.sel[this.tab] = [];
        this.syncUrl();
        this.render();
        this.announce(`${cap(this.cat.nounPlural)} cleared.`);
        this.input.focus();
      });
    }

    setTab(id) {
      if (!this.catById(id) || id === this.tab) return;
      this.tab = id;
      this.syncUrl();
      this.render();
    }

    onKey(e) {
      const opts = [...this.list.querySelectorAll('[role="option"]')];
      if (e.key === 'Escape') { this.closeList(); return; }
      if (!opts.length || this.list.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const dir = e.key === 'ArrowDown' ? 1 : -1;
        this.active = (this.active + dir + opts.length) % opts.length;
        opts.forEach((o, i) => o.setAttribute('aria-selected', i === this.active ? 'true' : 'false'));
        this.input.setAttribute('aria-activedescendant', opts[this.active].id);
        opts[this.active].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && this.active > -1) {
        e.preventDefault();
        this.pick(opts[this.active]);
      }
    }

    /* ---------- search ---------- */
    async search(term) {
      this.term = term;
      if (term.length < 2) { this.closeList(); return; }
      const url = `${ROOT}search/suggest.json?q=${encodeURIComponent(term)}` +
        '&resources[type]=product&resources[limit]=10' +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,vendor,variants.sku';
      let products = [];
      try {
        const r = await fetch(url);
        const d = await r.json();
        products = (d && d.resources && d.resources.results && d.resources.results.products) || [];
      } catch (e) { /* network error: show no-results state */ }
      if (term !== this.term) return; // a newer search is in flight

      const chosen = this.allHandles();
      const hits = products
        .map((p) => ({ p, c: this.catForType(p.type) }))
        .filter(({ p, c }) => c && !this.exclude.some((x) => lower(p.title).includes(x)) && !chosen.has(p.handle));
      // Current tab's matches first
      hits.sort((a, b) => (a.c.id === this.tab ? 0 : 1) - (b.c.id === this.tab ? 0 : 1));
      this.renderOptions(hits, term);
    }

    renderOptions(hits, term) {
      this.list.replaceChildren();
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      const multi = this.cats.length > 1;
      if (!hits.length) {
        this.list.append(el('li', {
          class: 'mc__noresult',
          role: 'presentation',
          text: `Nothing matches “${term}”. Try a brand, like Rocket or Eureka.`,
        }));
      } else {
        hits.forEach(({ p, c }, i) => {
          const img = (p.featured_image && p.featured_image.url) || p.image;
          const meta = [p.vendor, multi ? cap(c.noun) : null].filter(Boolean).join(', ');
          this.list.append(el('li', {
            role: 'option',
            id: `${this.list.id}-${i}`,
            class: 'mc__option',
            'aria-selected': 'false',
            'data-handle': p.handle,
            'data-cat': c.id,
          },
          img ? el('img', { src: sized(img, 96), alt: '', width: 48, height: 48, loading: 'lazy' }) : el('span', { class: 'mc__option-ph' }),
          el('span', { class: 'mc__option-text' },
            el('span', { class: 'mc__option-title', text: p.title }),
            meta ? el('span', { class: 'mc__option-meta', text: meta }) : null)));
        });
      }
      this.list.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    closeList() {
      this.list.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
    }

    pick(opt) {
      this.input.value = '';
      this.closeList();
      this.add(opt.dataset.handle, opt.dataset.cat);
    }

    /* ---------- data ---------- */
    async loadFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const requests = [];
      this.cats.forEach((c) => {
        const raw = params.get(c.id);
        if (!raw) return;
        [...new Set(raw.split(',').map((s) => s.trim()).filter(Boolean))].slice(0, this.max)
          .forEach((h) => requests.push({ h, hint: c.id }));
      });
      const wantTab = params.get('tab');

      if (requests.length) {
        this.setBusy(true);
        const results = await Promise.all(requests.map((r) => this.fetchItem(r.h)));
        this.setBusy(false);
        let missing = 0;
        results.forEach((m, i) => {
          if (!m) { missing += 1; return; }
          const c = this.catForType(m.type) || this.catById(requests[i].hint);
          const list = this.sel[c.id];
          if (list.length < this.max && !list.some((x) => x.handle === m.handle)) list.push(m);
        });
        if (missing) this.announce('Some products in this link are no longer available.');
      }

      if (this.catById(wantTab)) this.tab = wantTab;
      else { const first = this.cats.find((c) => this.sel[c.id].length); if (first) this.tab = first.id; }
      this.syncUrl();
      this.render();
    }

    async fetchItem(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle);
      try {
        // No Accept: application/json header here. Shopify answers that header with its own
        // {"product": {...}} JSON and ignores ?view=, so the compare-data template never runs.
        const r = await fetch(`${ROOT}products/${encodeURIComponent(handle)}?view=compare-data`, {
          credentials: 'same-origin',
        });
        if (!r.ok) throw new Error(r.status);
        const data = JSON.parse(await r.text()); // throws if the handle fell back to the HTML template
        if (!data || !data.handle || !data.specs) throw new Error('Unexpected compare-data response');
        this.cache.set(handle, data);
        return data;
      } catch (e) {
        return null;
      }
    }

    async add(handle, hint) {
      if (!handle) return;
      if (this.allHandles().has(handle)) { this.announce('That one is already in your comparison.'); return; }
      const hinted = this.catById(hint) || this.cat;
      if (this.sel[hinted.id].length >= this.max) {
        this.setTab(hinted.id);
        this.announce(`You can compare up to ${this.max} ${hinted.nounPlural}. Remove one to add another.`);
        return;
      }
      this.setBusy(true);
      const m = await this.fetchItem(handle);
      this.setBusy(false);
      if (!m) { this.announce('That product couldn’t be loaded. Try another.'); return; }
      const c = this.catForType(m.type) || hinted;
      const list = this.sel[c.id];
      if (list.length >= this.max) {
        this.setTab(c.id);
        this.announce(`You can compare up to ${this.max} ${c.nounPlural}. Remove one to add another.`);
        return;
      }
      if (list.some((x) => x.handle === m.handle)) return;
      list.push(m);
      const switched = c.id !== this.tab;
      this.tab = c.id;
      this.syncUrl();
      this.render();
      this.announce(`${m.title} added${switched ? ` to ${c.label}` : ''}. ${list.length} of ${this.max} selected.`);
    }

    remove(handle) {
      const list = this.items;
      const m = list.find((x) => x.handle === handle);
      this.sel[this.tab] = list.filter((x) => x.handle !== handle);
      this.syncUrl();
      this.render();
      if (m) this.announce(`${m.title} removed.`);
      this.input.focus({ preventScroll: true });
    }

    syncUrl() {
      const params = new URLSearchParams(window.location.search);
      this.cats.forEach((c) => params.delete(c.id));
      params.delete('tab');
      const parts = [params.toString()];
      this.cats.forEach((c) => {
        const hs = this.sel[c.id].map((m) => encodeURIComponent(m.handle)).join(',');
        if (hs) parts.push(`${c.id}=${hs}`);
      });
      const firstWithItems = this.cats.find((c) => this.sel[c.id].length);
      const defaultTab = firstWithItems ? firstWithItems.id : this.cats[0].id;
      if (this.tab !== defaultTab) parts.push(`tab=${this.tab}`);
      const qs = parts.filter(Boolean).join('&');
      window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    }

    value(m, source) {
      return m.specs ? m.specs[source] : undefined;
    }

    /* ---------- rendering ---------- */
    render() {
      const c = this.cat;
      const n = this.items.length;

      this.tabs.forEach((t) => {
        const on = t.dataset.tab === this.tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on) this.panel.setAttribute('aria-labelledby', t.id);
        const badge = t.querySelector('[data-tabcount]');
        const k = this.sel[t.dataset.tab].length;
        if (badge) badge.textContent = k ? `(${k})` : '';
      });

      this.root.querySelectorAll('[data-popular-for]').forEach((p) => { p.hidden = p.dataset.popularFor !== this.tab; });
      const chosen = this.allHandles();
      this.root.querySelectorAll('.mc__popular [data-add-handle]').forEach((b) => { b.hidden = chosen.has(b.dataset.addHandle); });

      this.empty.hidden = n > 0;
      this.emptyTitle.textContent = `Pick two to ${this.max} ${c.nounPlural} to see them side by side.`;
      this.scroller.hidden = n === 0;
      this.toolbar.hidden = n === 0;
      this.count.textContent = `${n} of ${this.max} ${c.nounPlural} selected`;
      this.clearBtn.textContent = `Clear ${c.nounPlural}`;
      this.diff.disabled = n < 2;
      if (n < 2 && this.diffOnly) { this.diffOnly = false; this.diff.checked = false; }
      this.caption.textContent = `${c.label} comparison`;

      const allFull = this.cats.every((x) => this.sel[x.id].length >= this.max);
      this.input.disabled = allFull;

      this.table.style.setProperty('--mc-cols', n + (n < this.max ? 1 : 0));
      this.renderHead();
      this.renderBody();
      this.renderSuggest();
    }

    renderHead() {
      const tr = el('tr', {}, el('td', { class: 'mc__corner' }));
      this.items.forEach((m) => tr.append(this.headCell(m)));
      if (this.items.length < this.max) {
        tr.append(el('td', { class: 'mc__addcol' },
          el('button', { type: 'button', class: 'mc__addbtn', 'data-focus-search': true },
            el('span', { class: 'mc__plus', 'aria-hidden': 'true', text: '+' }),
            `Add a ${this.cat.noun}`)));
      }
      this.thead.replaceChildren(tr);
    }

    headCell(m) {
      const price = el('p', { class: 'mc__price' },
        el('span', { text: (m.price_varies ? 'From ' : '') + m.price }),
        m.compare_at_price
          ? el('s', {}, el('span', { class: 'mc__sr', text: 'Regular price ' }), m.compare_at_price)
          : null);

      const buy = m.available
        ? el('form', { action: `${ROOT}cart/add`, method: 'post', class: 'mc__buy' },
          el('input', { type: 'hidden', name: 'id', value: m.variant_id }),
          el('input', { type: 'hidden', name: 'quantity', value: '1' }),
          el('button', { type: 'submit', class: 'mc__cta', text: 'Add to Cart' }))
        : el('p', { class: 'mc__soldout', text: 'Sold out' });

      return el('th', { scope: 'col', class: 'mc__machine' },
        el('button', { type: 'button', class: 'mc__remove', 'data-remove': m.handle, 'aria-label': `Remove ${m.title}`, text: '×' }),
        el('a', { href: m.url, class: 'mc__media', tabindex: '-1', 'aria-hidden': 'true' },
          m.image ? el('img', { src: m.image, alt: '', width: 240, height: 240, loading: 'lazy' }) : null),
        m.vendor ? el('p', { class: 'mc__vendor', text: m.vendor }) : null,
        el('a', { href: m.url, class: 'mc__name', text: m.title }),
        price,
        buy);
    }

    renderBody() {
      const items = this.items;
      const n = items.length;
      const span = n + 1 + (n < this.max ? 1 : 0);
      const groups = [];
      this.rows
        .filter((r) => !r.category || r.category === 'both' || r.category === this.tab)
        .forEach((r) => {
          let g = groups.find((x) => x.name === r.group);
          if (!g) { g = { name: r.group, rows: [] }; groups.push(g); }
          g.rows.push(r);
        });

      const bodies = [];
      groups.forEach((g) => {
        const visible = g.rows
          .map((r) => ({ r, vals: items.map((m) => this.value(m, r.source)) }))
          .filter(({ vals }) => vals.some((v) => !isBlank(v)))
          .map((x) => ({ ...x, differs: n > 1 && new Set(x.vals.map(keyOf)).size > 1 }))
          .filter((x) => !this.diffOnly || x.differs);
        if (!visible.length) return;

        const tb = el('tbody', { class: 'mc__group' },
          el('tr', { class: 'mc__grouprow' },
            el('th', { scope: 'colgroup', colspan: span }, el('span', { text: g.name }))));
        visible.forEach(({ r, vals, differs }) => {
          const tr = el('tr', { class: differs ? 'mc__row is-different' : 'mc__row' },
            el('th', { scope: 'row', class: 'mc__rowlabel', text: r.label }));
          vals.forEach((v) => tr.append(this.cell(r, v)));
          if (n < this.max) tr.append(el('td', { class: 'mc__filler' }));
          tb.append(tr);
        });
        bodies.push(tb);
      });

      if (!bodies.length && n > 0) {
        bodies.push(el('tbody', {},
          el('tr', {}, el('td', {
            colspan: span,
            class: 'mc__allsame',
            text: this.diffOnly ? `These ${this.cat.nounPlural} match on every listed spec.` : 'No specs are listed for these yet.',
          }))));
      }

      this.table.replaceChildren(this.caption, this.thead, ...bodies);
    }

    cell(r, v) {
      const td = el('td');
      if (isBlank(v)) {
        td.append(el('span', { class: 'mc__na', 'aria-hidden': 'true', text: '—' }), el('span', { class: 'mc__sr', text: 'Not listed' }));
        return td;
      }
      switch (r.display) {
        case 'yesno':
          if (YES.test(String(v))) td.append(checkIcon(), el('span', { class: 'mc__sr', text: 'Yes' }));
          else if (NO.test(String(v))) td.append(el('span', { class: 'mc__no', text: 'No' }));
          else td.textContent = v;
          break;
        case 'feature':
          if (typeof v === 'object') {
            td.append(el('div', { class: 'mc__feature' },
              v.icon ? el('img', { src: v.icon, alt: '', width: 28, height: 28, loading: 'lazy' }) : null,
              el('div', {},
                el('strong', { text: v.label || '' }),
                v.detail ? el('p', { text: v.detail }) : null)));
          } else td.textContent = v;
          break;
        case 'list': {
          const parts = String(v).split(/\s*[,\n]\s*/).filter(Boolean);
          td.append(el('ul', { class: 'mc__list' }, parts.map((t) => el('li', { text: t }))));
          break;
        }
        case 'rating':
          if (typeof v === 'object' && v.value) {
            const stars = el('span', { class: 'mc__stars', 'aria-hidden': 'true', text: '★★★★★' });
            stars.style.setProperty('--r', v.value);
            td.append(stars,
              el('span', { class: 'mc__sr', text: `Rated ${v.value} out of 5. ` }),
              el('span', { text: `${v.value} (${v.count} ${v.count === 1 ? 'review' : 'reviews'})` }));
          } else td.textContent = typeof v === 'object' ? '' : v;
          break;
        default:
          td.textContent = typeof v === 'object' ? (v.label || '') : v;
      }
      return td;
    }

    renderSuggest() {
      this.suggest.replaceChildren();
      const items = this.items;
      if (items.length !== 1) { this.suggest.hidden = true; return; }
      const base = items[0];
      const chosen = this.allHandles();
      const list = (base.compare_list || []).filter((p) => !chosen.has(p.handle));
      if (!list.length) { this.suggest.hidden = true; return; }
      this.suggest.append(
        el('p', { class: 'mc__suggest-label', text: `Often compared with the ${base.title}:` }),
        el('div', { class: 'mc__chips' },
          list.map((p) => el('button', { type: 'button', class: 'mc__chip', 'data-add-handle': p.handle, 'data-add-cat': this.tab }, `+ ${p.title}`))));
      this.suggest.hidden = false;
    }

    /* ---------- utilities ---------- */
    setBusy(on) {
      this.root.classList.toggle('is-loading', on);
      this.root.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    announce(msg) {
      this.status.textContent = '';
      window.setTimeout(() => { this.status.textContent = msg; }, 30);
    }

    async share(btn) {
      const label = btn.textContent;
      try {
        await navigator.clipboard.writeText(window.location.href);
        btn.textContent = 'Link copied';
      } catch (e) {
        btn.textContent = 'Couldn’t copy. Use the address bar.';
      }
      window.setTimeout(() => { btn.textContent = label; }, 2200);
    }
  }

  const init = (scope) => scope.querySelectorAll('[data-machine-compare]').forEach((r) => new ProductCompare(r));
  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
